import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { styles } from "@insite/content-library/Widgets/ContactUs/AvailableTopics";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { GridItem, Typography } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusIcon from "@tCentro/Icons/MinusIcon";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    brandAccordianMainTitle = "brandAccordianMainTitle",
    brandAccordianText = "brandAccordianText",
    checkboxFieldValue = "checkboxFieldValue",
    brandModelData = "brandModelData",
    cssClass = "cssClass",
}

interface BrandModelData {
    fields: {
        brandAccordianTitle: string;
        brandAccordianDescription: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.brandAccordianMainTitle]: string;
        [fields.brandAccordianText]: string;
        [fields.checkboxFieldValue]: boolean;
        [fields.brandModelData]: BrandModelData[];
        [fields.cssClass]: string;
    };
}

export interface CENBrandAccordianStyles {
    accordianSection?: AccordionSectionPresentationProps;
    brandAccordianTextStyles?: TypographyPresentationProps;
    brandAccordainMainTitleStyles?: InjectableCss;
    brandContainerTextStyles?: InjectableCss;
    mainContainer?: InjectableCss;
}

export const cenBrandAccordianStyles: CENBrandAccordianStyles = {
    mainContainer: {
        css: css`
            width: 90%;
            margin-left: auto;
            margin-right: auto;
            @media (min-width: 992px) and (max-width: 1200px) {
                width: 97%;
            }
            margin-top: 96px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 56px;
                        width: 100%;
                    `,
                    css`
                        margin-top: 56px;
                        width: 97%;
                    `,
                    css`
                        margin-top: 56px;
                        width: 97%;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    accordianSection: {
        toggleIconProps: {
            src: Minus,
        },
        expandIconProps: {
            src: Plus,
        },
        panelProps: {
            css: css`
                border-bottom: 1px solid #e0dfe0;
                border-left: none;
                border-right: none;
            `,
        },
        headerProps: {
            css: css`
                button[aria-expanded="true"] {
                    > span {
                        padding: 24px 0px;
                    }
                }
                button[aria-expanded="false"] {
                    > span {
                        padding: 12px 0px;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    max-width: 85%;
                                `,
                                null,
                                null,
                                null,
                                null,
                            ])}
                    }
                }
                button {
                    border-top: 1px solid #e0dfe0;
                    border-left: none;
                    border-right: none;
                    border-bottom: 1px solid #e0dfe0;
                    background-color: #ffffff;

                    > span {
                        font-size: ${TYPOGRAPHY.size.largest};
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        line-height: 32px;
                        color: ${COLORS.text.mineShaft};
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(theme, [
                                css`
                                    font-size: ${TYPOGRAPHY.size.large};
                                `,
                                css`
                                    font-size: ${TYPOGRAPHY.size.large};
                                `,
                                null,
                                null,
                                null,
                            ])}
                        svg {
                            height: 19.5px;
                            width: 19.5px;
                        }
                    }
                    &:focus {
                        border-top: 1px solid #e0dfe0;
                        border-bottom: 1px solid #e0dfe0;
                        border-left: none;
                        border-right: none;
                        // border-bottom: none;
                    }
                    &[aria-expanded="true"] {
                        border-bottom: none;
                        // border-bottom: 1px solid #e0dfe0;
                    }

                }
            `,
        },
    },
    brandAccordainMainTitleStyles: {
        css: css`
            margin-bottom: 32px;
            font-size: ${TYPOGRAPHY.size.xxlarge};
            color: ${COLORS.text.mineShaft};
            font-weight: 700;
            line-height: 40px;
        `,
    },
    brandContainerTextStyles: {
        css: css`
            margin-bottom: 40px;
        `,
    },
    brandAccordianTextStyles: {
        css: css`
            p {
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
            }
        `,
    },
};

export const CENBrandAccordian: FC<OwnProps> = ({ fields }) => {
    const [expandedState, setExpandedState] = useState<boolean[]>(
        fields.brandModelData.map(() => fields.checkboxFieldValue ?? false),
    );

    const toggleAccordion = (index: number) => {
        setExpandedState(prev => {
            const newState = [...prev];
            newState[index] = !newState[index];
            return newState;
        });
    };

    return (
        <StyledWrapper {...cenBrandAccordianStyles.mainContainer} className={fields.cssClass}>
            <StyledWrapper {...cenBrandAccordianStyles.brandAccordainMainTitleStyles}>
                {fields.brandAccordianMainTitle}
            </StyledWrapper>

            <StyledWrapper {...cenBrandAccordianStyles.brandContainerTextStyles}>
                <Typography {...cenBrandAccordianStyles.brandAccordianTextStyles}>
                    {parse(transformContentHtml(fields.brandAccordianText), parserOptions)}
                </Typography>
            </StyledWrapper>

            {fields.brandModelData.map((item, index) => (
                <Accordion headingLevel={3} key={index}>
                    <AccordionSection
                        title={item.fields.brandAccordianTitle}
                        {...cenBrandAccordianStyles.accordianSection}
                        expanded={expandedState[index]}
                        onTogglePanel={() => toggleAccordion(index)}
                    >
                        <StyledWrapper>
                            <Typography>
                                {parse(transformContentHtml(item.fields.brandAccordianDescription), parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </AccordionSection>
                </Accordion>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CENBrandAccordian,
    definition: {
        group: "Basic",
        displayName: "Brand Accordian",
        fieldDefinitions: [
            {
                name: fields.brandAccordianMainTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.brandAccordianText,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.checkboxFieldValue,
                displayName: "Default Expanded",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.brandModelData,
                editorTemplate: "ListField",
                fieldType: "Translatable",
                getDisplay: (item: HasFields) => {
                    return item.fields.brandAccordianTitle;
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "brandAccordianTitle",
                        editorTemplate: "TextField",
                        displayName: "Brand Accordian Title",
                        defaultValue: "",
                    },
                    {
                        name: "brandAccordianDescription",
                        editorTemplate: "RichTextField",
                        displayName: "Brand Description",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
